/* eslint-disable no-param-reassign */
import PlannerMonth, { PlannerMonthProps } from '@oberoninternal/travelbase-ds/components/calendar/PlannerMonth';
import { Box, Flex } from '@rebass/grid';
import React, { ComponentType, createContext, MutableRefObject, PropsWithChildren, useCallback } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Locale } from '../entities/Locale';
import usePlannerView, {
    GUTTER_SIZE,
    PlannerViewActions,
    PlannerViewOptions,
    PlannerViewState,
} from '../hooks/planner/usePlannerView';
import { getDateFnsLocale } from '../utils/localeHelpers';

export type PlannerMonthInstance = HTMLDivElement & { plannerViewActions: PlannerViewActions };
interface Props<T extends PlannerMonthProps> extends PlannerViewOptions {
    loading?: boolean;
    monthsRef?: MutableRefObject<PlannerMonthInstance | null>;
    PlannerMonthComponent?: ComponentType<React.PropsWithChildren<T>>;
    isMonthLoaded?: (date: Date) => boolean;
    isActivity?: boolean;
}

export const PlannerViewContext = createContext<PlannerViewState | null>(null);
export const PlannerViewActionsContext = createContext<PlannerViewActions | null>(null);

const Planner = <T extends PlannerMonthProps>({
    children,
    initialCenterMonth,
    onClickOutside,
    monthsRef,
    isMonthLoaded: monthLoaded,
    isActivity,
    PlannerMonthComponent,
    onInViewIntervalChange,
    ...rest
}: PropsWithChildren<Props<T> & Omit<T, 'date'>>) => {
    const MonthComponent = (PlannerMonthComponent ?? PlannerMonth) as ComponentType<
        React.PropsWithChildren<PlannerMonthProps>
    >;
    const intl = useIntl();
    const [viewState, plannerViewActions] = usePlannerView({
        onInViewIntervalChange,
        onClickOutside,
        initialCenterMonth,
        mode: isActivity ? 'horizontal' : undefined,
    });

    const { months, viewportRef: viewportCallbackRef } = viewState;
    const mergedRefs = useCallback(
        (node: HTMLDivElement) => {
            if (!node) {
                return;
            }
            viewportCallbackRef(node);
            if (monthsRef) {
                monthsRef.current = node as PlannerMonthInstance;
                monthsRef.current.plannerViewActions = plannerViewActions;
            }
        },
        [monthsRef, plannerViewActions, viewportCallbackRef]
    );
    return (
        <PlannerViewContext.Provider value={viewState}>
            <PlannerViewActionsContext.Provider value={plannerViewActions}>
                {children}
                <MonthsWrapper ref={mergedRefs} isActivity={isActivity}>
                    <Months isActivity={isActivity}>
                        {months.map(monthDate => (
                            <MonthComponent
                                date={monthDate}
                                key={+monthDate}
                                locale={getDateFnsLocale(intl.locale as Locale)}
                                loading={!!monthLoaded && !monthLoaded(monthDate)}
                                {...rest}
                            />
                        ))}
                    </Months>
                </MonthsWrapper>
            </PlannerViewActionsContext.Provider>
        </PlannerViewContext.Provider>
    );
};

export default Planner;

export const PlannerControlsWrapper = styled(Flex).attrs({
    width: 1,
    alignItems: 'center',
    flexDirection: ['column', null, 'row'],
    mt: [null],
    style: { flexShrink: 0 },
})``;

const Months = styled(Flex).attrs({ width: 1 })<{ isActivity?: boolean }>`
    --monthGap: ${GUTTER_SIZE};
    position: relative;
    > * {
        scroll-snap-align: start;
        cursor: drag;
    }

    @media screen and (max-width: ${({ theme }) => theme.mediaQueriesValues.s - 1}px) {
        flex-direction: ${p => (p.isActivity ? 'row' : 'column')};

        > * {
            margin-top: calc(var(--monthGap) * 1px);
        }
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        > * + * {
            margin-left: calc(var(--monthGap) * 1px);
        }
    }
`;

const MonthsWrapper = styled(Box).attrs({ width: 1, mb: [null, null, 5] })<{ isActivity?: boolean }>`
    position: relative;
    overflow: auto;
    scroll-snap-type: x mandatory;
    will-change: transform;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        overscroll-behavior-x: none;
    }
    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.s}) {
        margin-bottom: 80px;
    }
    white-space: nowrap;
    cursor: grab;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
        display: none;
    }
    scrollbar-width: 0;
    /* hide scrollbar on safari */
    -ms-overflow-style: none;
    @media screen and (max-width: ${({ theme }) => theme.mediaQueriesValues.s + 1}px) {
        flex: 1;
        padding: ${p => (p.isActivity ? '0' : '0 0.8rem')};
        overflow: ${p => (p.isActivity ? 'hidden' : 'auto')};
    }
`;

export const PlannerHeader = styled(Box)`
    @media screen and (max-width: ${({ theme }) => theme.mediaQueriesValues.s + 1}px) {
        box-shadow: 0 0 40px 0 rgba(16, 36, 48, 0.1);
    }
`;

export const PlannerContainer = styled(Box)`
    @media screen and (max-width: ${({ theme }) => theme.mediaQueriesValues.s + 1}px) {
        max-height: 100vh;
        height: 100%;
        display: flex;
        flex-direction: column;
    }
`;
